import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import ImageNotSupportedRoundedIcon from "@mui/icons-material/ImageNotSupportedRounded";
import AssignmentReturnRoundedIcon from "@mui/icons-material/AssignmentReturnRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import { useNavigate } from "react-router-dom";
import Title from "../components/header/Title";
import OrderService, { IOrder, IOrderDetail, ORDER_STATUS_OPTIONS } from "../services/OrderService";
import { resolveImageUrl, unwrapPage } from "../services/apiUtils";
import { normalizeOrderStatus, orderStatusLabel, orderStatusColor } from "../services/orderStatusUtils";

const ORDER_SEARCH_FIELDS: Array<{ label: string; apiField: string }> = [
  { label: "Order Number", apiField: "orderNumber" },
  { label: "Customer Name", apiField: "fullName" },
  { label: "Username", apiField: "userName" },
  { label: "Email", apiField: "userEmail" },
  { label: "Phone", apiField: "userPhone" },
  { label: "Mobile", apiField: "userMobile" },
  { label: "City", apiField: "userCity" },
];

const REFUND_REASONS = [
  "Damaged Product",
  "Wrong Item Received",
  "Quality Issue",
  "Not as Described",
  "I don't like it",
  "Others",
];

const safeStr = (v: any) => (v == null ? "" : String(v));
const normalizeStatus = normalizeOrderStatus;
const statusLabel = orderStatusLabel;
const statusColor = orderStatusColor;

const getOrderNo = (row: any) => row?.orderNumber ?? row?.orderId ?? "-";
const getCustomerName = (row: any) =>
  row?.userFName || row?.userName || row?.customerName || (row?.userId != null ? `User #${row.userId}` : "-");
const getCustomerEmail = (row: any) => row?.userEmail || row?.email || "-";
const getCustomerId = (row: any) => row?.userId ?? "-";
const getAddress = (row: any) => row?.userAdd || row?.fullAddress || row?.address || "-";
const getTotal = (row: any) => {
  const v = row?.orderTotalPrice ?? row?.totalAmount ?? row?.total;
  return v != null ? `Rs. ${v}` : "-";
};
const getOrderDetails = (row: any): IOrderDetail[] =>
  Array.isArray(row?.orderDetails) ? row.orderDetails : Array.isArray(row?.orderItems) ? row.orderItems : [];
const getItemCount = (row: any) => {
  const list = getOrderDetails(row);
  if (list.length) return list.length;
  if (row?.itemCount != null) return row.itemCount;
  return 1;
};

const getItemQuantity = (row: any) => {
  const list = getOrderDetails(row);
  if (list.length) {
    const total = list.reduce((sum, item) => sum + (Number(item?.productQty) || 0), 0);
    return total > 0 ? total : list.length;
  }
  if (row?.itemCount != null) return row.itemCount;
  return 1;
};
const getItemsTooltip = (row: any) => {
  const list = getOrderDetails(row);
  if (!list.length) return `${getItemQuantity(row)} item(s)`;
  return list
    .map((item) => `${item.productName || `Product #${item.productId ?? "-"}`} x ${item.productQty ?? 1}`)
    .join("\n");
};
const getPlacedOn = (row: any) => row?.orderDate ?? row?.createdAt ?? "-";
const getStatus = (row: any) => row?.orderStatus ?? row?.status ?? "ordered";
const getItemStatus = (item: IOrderDetail) => item?.orderDetailStatus ?? item?.status;
const getItemTotal = (item: IOrderDetail) => {
  const price = item?.productPrice;
  const qty = item?.productQty;
  if (price == null) return "-";
  return `Rs. ${price}${qty ? ` x ${qty}` : ""}`;
};

const PROGRESS_SEQUENCE = [7, 1, 2, 3, 4];

const computeAggregateOrderStatus = (itemStatuses: number[]): number => {
  if (itemStatuses.length === 0) return 1;

  const allSame = itemStatuses.every((s) => s === itemStatuses[0]);
  if (allSame) return itemStatuses[0];

  const allCancelled = itemStatuses.every((s) => s === 6);
  if (allCancelled) return 6;

  const hasDelivered = itemStatuses.some((s) => s === 4);
  if (hasDelivered) return 8;

  const activeRanks = itemStatuses
    .map((s) => PROGRESS_SEQUENCE.indexOf(s))
    .filter((r) => r !== -1);
  if (activeRanks.length > 0) {
    return PROGRESS_SEQUENCE[Math.min(...activeRanks)];
  }

  return itemStatuses[0];
};

type TabKey = "all" | "confirmed" | "processing" | "shipped" | "delivered" | "cancelled" | "pending";

const TABS: Array<{ key: TabKey; label: string; statusId?: number }> = [
  { key: "all", label: "All Orders" },
  { key: "confirmed", label: "Confirmed", statusId: 1 },
  { key: "processing", label: "Processing", statusId: 2 },
  { key: "shipped", label: "Shipped", statusId: 3 },
  { key: "delivered", label: "Delivered", statusId: 4 },
  { key: "cancelled", label: "Cancelled", statusId: 6 },
  { key: "pending", label: "Pending", statusId: 7 },
];

type StatusTarget = { order: IOrder; item?: IOrderDetail };

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewOrder, setViewOrder] = useState<IOrder | null>(null);
  const [statusTarget, setStatusTarget] = useState<StatusTarget | null>(null);
  const [newStatus, setNewStatus] = useState<number>(1);
  const [courierId, setCourierId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<TabKey>("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [searchField, setSearchField] = useState(ORDER_SEARCH_FIELDS[0].apiField);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearchField, setAppliedSearchField] = useState(ORDER_SEARCH_FIELDS[0].apiField);
  const [searchText, setSearchText] = useState("");

  const [refundTarget, setRefundTarget] = useState<StatusTarget | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundOtherReason, setRefundOtherReason] = useState("");
  const [refundQty, setRefundQty] = useState<number>(1);
  const [refundSaving, setRefundSaving] = useState(false);
  const [refundError, setRefundError] = useState<string | null>(null);

  const loadOrders = () => {
    setLoading(true);
    setError(null);
    const tabDef = TABS.find((t) => t.key === tab);
    OrderService.filterOrders({
      page,
      size: pageSize,

      ...(tabDef?.statusId != null ? { orderStatus: tabDef.statusId } : {}),
      ...(searchText ? { [appliedSearchField]: searchText } : {}),
    })
      .then((res: any) => {
        const { content, totalElements } = unwrapPage<IOrder>(res);
        setOrders(content);
        setTotalCount(totalElements);
      })
      .catch((err: any) => {
        setOrders([]);
        setTotalCount(0);
        setError(
          err?.friendlyMessage ||
            "Orders could not be loaded. Please make sure the Orders API is reachable."
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadOrders();

  }, [page, pageSize, tab, searchText, appliedSearchField]);

  const runSearch = () => {
    setPage(0);
    setAppliedSearchField(searchField);
    setSearchText(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setPage(0);
    setAppliedSearchField(searchField);
    setSearchText("");
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runSearch();
    }
  };

  const activeTabDef = TABS.find((t) => t.key === tab);
  const visibleOrders =
    activeTabDef?.statusId != null
      ? orders.filter((o) => normalizeStatus(getStatus(o)) === activeTabDef.statusId)
      : orders;

  useEffect(() => {
    if (!viewOrder) return;
    const fresh = orders.find((o) => o.orderId === viewOrder.orderId);
    if (fresh) setViewOrder(fresh);

  }, [orders]);

  const openView = (order: IOrder) => setViewOrder(order);

  const openRefundDialog = (order: IOrder, item: IOrderDetail) => {
    setRefundTarget({ order, item });
    setRefundReason("");
    setRefundOtherReason("");
    setRefundQty(item?.productQty ? Number(item.productQty) : 1);
    setRefundError(null);
  };

  const handleRefundSave = async () => {
    if (!refundTarget?.item) return;
    const orderDetailId = refundTarget.item.orderDetailId;
    if (orderDetailId == null) {
      setRefundError("This item has no orderDetailId, so a refund can't be filed for it.");
      return;
    }
    const reason = refundReason === "Others" ? refundOtherReason.trim() : refundReason;
    if (!reason) {
      setRefundError("Please select (or enter) a reason for the refund.");
      return;
    }
    setRefundSaving(true);
    setRefundError(null);
    try {
      await OrderService.createRefund({
        orderId: refundTarget.order.orderId,
        reason,
        items: [{ orderDetailId, quantityRefunded: refundQty || 1 }],
      });
      setRefundTarget(null);
      loadOrders();
    } catch (err: any) {
      setRefundError(err?.friendlyMessage || err?.message || "Could not submit the refund request.");
    } finally {
      setRefundSaving(false);
    }
  };

  const openStatusDialog = (order: IOrder, item?: IOrderDetail) => {
    setStatusTarget({ order, item });
    setNewStatus(normalizeStatus(item ? getItemStatus(item) : getStatus(order)));
    setCourierId("");
  };

  const handleStatusSave = async () => {
    if (!statusTarget) return;
    setSaving(true);
    try {
      if (statusTarget.item) {
        const orderDetailId = statusTarget.item.orderDetailId;
        if (orderDetailId == null) {
          throw new Error("This item has no orderDetailId, so it can't be updated individually.");
        }
        await OrderService.updateOrderDetailStatus(
          orderDetailId,
          newStatus,
          courierId ? Number(courierId) : undefined
        );

        try {
          const allItemStatuses = getOrderDetails(statusTarget.order).map((it) =>
            it.orderDetailId === orderDetailId ? newStatus : normalizeStatus(getItemStatus(it))
          );
          const aggregateStatus = computeAggregateOrderStatus(allItemStatuses);
          await OrderService.updateOrderStatus(statusTarget.order.orderId, aggregateStatus);
        } catch (cascadeErr: any) {
          setError(
            cascadeErr?.friendlyMessage ||
              cascadeErr?.message ||
              "Item status was updated, but the whole order's status could not be refreshed."
          );
        }
      } else {
        await OrderService.updateOrderStatus(statusTarget.order.orderId, newStatus);
      }
      setStatusTarget(null);
      loadOrders();
    } catch (err: any) {
      setError(err?.friendlyMessage || err?.message || "Could not update order status.");
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "orderNo",
      headerName: "Order No",
      flex: 1,
      minWidth: 170,
      valueGetter: (params: any) => getOrderNo(params.row),
      renderCell: (params: any) => (
        <Tooltip title={`#${getOrderNo(params.row)}`}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            #{getOrderNo(params.row)}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 130,
      valueGetter: (params: any) => getCustomerName(params.row),
    },
    {
      field: "items",
      headerName: "Quantity",
      flex: 0.5,
      minWidth: 90,
      sortable: false,
      valueGetter: (params: any) => getItemQuantity(params.row),
      renderCell: (params: any) => (
        <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{getItemsTooltip(params.row)}</span>}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "total",
      headerName: "Total",
      flex: 0.7,
      valueGetter: (params: any) => getTotal(params.row),
    },
    {
      field: "placedOn",
      headerName: "Placed On",
      flex: 1,
      valueGetter: (params: any) => getPlacedOn(params.row),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      renderCell: (params: any) => {
        const s = getStatus(params.row);
        return <Chip label={statusLabel(s)} color={statusColor(s) as any} size="small" />;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="View order details">
            <IconButton size="small" color="primary" onClick={() => openView(params.row)}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditRoundedIcon fontSize="small" />}
            onClick={() => openStatusDialog(params.row)}
          >
            Status
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Title title="Orders" subtitle="View and manage customer orders" />
        <Button
          variant="contained"
          startIcon={<AddShoppingCartRoundedIcon />}
          onClick={() => navigate("/orders/create")}
        >
          New Order
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          mb: 2.5,
          mt: 1,
        }}
      >
        <Select
          size="small"
          value={searchField}
          onChange={(e) => setSearchField(e.target.value as string)}
          sx={{ minWidth: 170, bgcolor: "#fff" }}
        >
          {ORDER_SEARCH_FIELDS.map((f) => (
            <MenuItem key={f.apiField} value={f.apiField}>
              {f.label}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          placeholder={`Search by ${ORDER_SEARCH_FIELDS.find((f) => f.apiField === searchField)?.label || "..."} — press Enter`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          sx={{ minWidth: 280, bgcolor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton size="small" onClick={runSearch} edge="start">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {searchText && (
          <Button size="small" onClick={clearSearch}>
            Clear Search
          </Button>
        )}
      </Box>

      <Box sx={{ bgcolor: "#fff", borderRadius: "12px 12px 0 0", overflow: "hidden", mb: 0.5 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => {
            setTab(v);
            setPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          {TABS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={t.label}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ height: 600, width: "100%", bgcolor: "#fff", mt: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : visibleOrders.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8, color: "grey.600" }}>
            No orders in this tab.
          </Box>
        ) : (
          <DataGrid
            rows={visibleOrders}
            columns={columns}
            getRowId={(row) => row.orderId ?? row.id ?? Math.random()}
            paginationMode="server"
            rowCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            onRowDoubleClick={(params: any) => openView(params.row)}
          />
        )}
      </Box>

      <Dialog open={!!viewOrder} onClose={() => setViewOrder(null)} maxWidth="sm" fullWidth>
        {viewOrder && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Order #{getOrderNo(viewOrder)}</span>
              <Chip
                label={statusLabel(getStatus(viewOrder))}
                color={statusColor(getStatus(viewOrder)) as any}
                size="small"
              />
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customer
              </Typography>
              <Stack spacing={0.75} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonRoundedIcon fontSize="small" color="action" />
                  <Typography variant="body2">{getCustomerName(viewOrder)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <BadgeRoundedIcon fontSize="small" color="action" />
                  <Typography variant="body2">User ID: {getCustomerId(viewOrder)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailRoundedIcon fontSize="small" color="action" />
                  <Typography variant="body2">{getCustomerEmail(viewOrder)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PlaceRoundedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                  <Typography variant="body2">{getAddress(viewOrder)}</Typography>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Order Summary
              </Typography>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="body2">Order No: #{getOrderNo(viewOrder)}</Typography>
                <Typography variant="body2">Order ID: {viewOrder.orderId}</Typography>
                <Typography variant="body2">Placed On: {getPlacedOn(viewOrder)}</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  Total: {getTotal(viewOrder)}
                </Typography>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Items ({getOrderDetails(viewOrder).length || getItemCount(viewOrder)})
                </Typography>
                <Button size="small" onClick={() => openStatusDialog(viewOrder)}>
                  Update Whole Order Status
                </Button>
              </Stack>

              <Stack spacing={1.25}>
                {getOrderDetails(viewOrder).length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No line-item detail was returned for this order.
                  </Typography>
                )}
                {getOrderDetails(viewOrder).map((item, idx) => (
                  <Box
                    key={item.orderDetailId ?? idx}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 1.25,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      src={resolveImageUrl(item.productImgUrl) || undefined}
                      sx={{ width: 44, height: 44, bgcolor: "grey.100" }}
                    >
                      <ImageNotSupportedRoundedIcon fontSize="small" color="disabled" />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {item.productName || `Product #${item.productId ?? "-"}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getItemTotal(item)}
                      </Typography>
                    </Box>
                    <Chip
                      label={statusLabel(getItemStatus(item))}
                      color={statusColor(getItemStatus(item)) as any}
                      size="small"
                    />
                    <Tooltip title="File a refund/return for this item">
                      <IconButton size="small" color="warning" onClick={() => openRefundDialog(viewOrder, item)}>
                        <AssignmentReturnRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Update status for this order (product-level view)">
                      <IconButton size="small" onClick={() => openStatusDialog(viewOrder, item)}>
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewOrder(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!statusTarget} onClose={() => setStatusTarget(null)}>
        <DialogTitle>
          Update Order #{statusTarget ? getOrderNo(statusTarget.order) : ""} Status
        </DialogTitle>
        <DialogContent>
          {statusTarget?.item ? (
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
              <Avatar
                variant="rounded"
                src={resolveImageUrl(statusTarget.item.productImgUrl) || undefined}
                sx={{ width: 36, height: 36, bgcolor: "grey.100" }}
              >
                <ImageNotSupportedRoundedIcon fontSize="small" color="disabled" />
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {statusTarget.item.productName || "Product"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getItemTotal(statusTarget.item)}
                </Typography>
              </Box>
            </Box>
          ) : (
            statusTarget && getItemCount(statusTarget.order) > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {getItemCount(statusTarget.order)} item(s) • {getTotal(statusTarget.order)}
              </Typography>
            )
          )}

          {statusTarget?.item && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This updates the status of only this line item (not the whole order).
            </Alert>
          )}

          <Select
            fullWidth
            value={newStatus}
            onChange={(e) => setNewStatus(Number(e.target.value))}
            sx={{ mt: 1, minWidth: 240 }}
          >
            {ORDER_STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>

          {statusTarget?.item && (
            <TextField
              fullWidth
              label="Courier ID (optional)"
              type="number"
              value={courierId}
              onChange={(e) => setCourierId(e.target.value)}
              sx={{ mt: 2 }}
              helperText="Only needed if this status update should assign/record a courier."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleStatusSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!refundTarget} onClose={() => setRefundTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Submit Refund Request</DialogTitle>
        <DialogContent>
          {refundTarget?.item && (
            <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1.25 }}>
              <Avatar
                variant="rounded"
                src={resolveImageUrl(refundTarget.item.productImgUrl) || undefined}
                sx={{ width: 36, height: 36, bgcolor: "grey.100" }}
              >
                <ImageNotSupportedRoundedIcon fontSize="small" color="disabled" />
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {refundTarget.item.productName || "Product"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Order #{getOrderNo(refundTarget.order)}
                </Typography>
              </Box>
            </Box>
          )}

          {refundError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {refundError}
            </Alert>
          )}

          <Select
            fullWidth
            displayEmpty
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value as string)}
            sx={{ mt: 0.5 }}
          >
            <MenuItem value="" disabled>
              Select refund reason
            </MenuItem>
            {REFUND_REASONS.map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </Select>

          {refundReason === "Others" && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Please specify your reason"
              value={refundOtherReason}
              onChange={(e) => setRefundOtherReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}

          <TextField
            fullWidth
            type="number"
            label="Quantity to refund"
            value={refundQty}
            onChange={(e) => setRefundQty(Math.max(1, Number(e.target.value) || 1))}
            inputProps={{ min: 1 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRefundSave} disabled={refundSaving}>
            {refundSaving ? <CircularProgress size={20} /> : "Submit Refund"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
